import { FC } from 'react';
import { useDesktop } from '@utils/hooks/useDesktop';
import { useAgentChatGate } from '@hooks/useAgentChatGate';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { AGENT_CHAT_FEATURE_KEY } from '@utils/featureRollout/constants';
import { PaulinaInvite } from './PaulinaInvite';
import { usePaulinaShouldYield } from './usePaulinaShouldYield';
import { markOpenOnMount } from './openOnMount';

/**
 * PaulinaInviteHost — host de la INVITACIÓN al rollout de Paulina ("Conoce a Paulina, pruébala").
 *
 * IMPORTANTE: vive FUERA de PaulinaGate/PaulinaProvider. En la variante `control` (aún no aceptó)
 * el provider NO se monta (PaulinaGate solo monta con offered=true), así que la invitación NO
 * puede vivir dentro del AgentChatWidget — debe renderizarse aquí, a nivel Layout (dentro de
 * NotificationProvider, para que usePaulinaShouldYield tenga las notificaciones). Mismo reparto
 * que la feature de referencia (BLE: el coachmark de invitación vive en la página, independiente
 * de offered; el revert/feedback viven con la feature montada).
 *
 * Cede ante pedidos entrantes (no se monta si hay pedido) igual que el nudge. Al aceptar, el gate
 * cambia la variante a treatment → offered pasa a true → PaulinaGate monta el provider y Paulina
 * aparece. Como el provider AÚN NO existe en el tick del accept, NO podemos llamar setOpen aquí;
 * en su lugar dejamos una señal one-shot en sessionStorage (markOpenOnMount) ANTES de gate.accept,
 * que el chat consume al montar (consumeOpenOnMount en usePaulinaChat) para abrirse solo. Así
 * "Probarla" activa la feature Y abre el chat sin que el comercio tenga que buscar la pill/avatar.
 */

export const PaulinaInviteHost: FC = () => {
  const isDesktop = useDesktop();
  const gate = useAgentChatGate();
  // `inviteSurface` ('card' | 'modal' | 'none') lo gobierna el backend y NO lo expone el contrato
  // FeatureGate (solo invitación booleana) → se lee del feature-version directamente, como en menú.
  const { inviteSurface } = useFeatureVersion(AGENT_CHAT_FEATURE_KEY);
  const { hayPedido } = usePaulinaShouldYield();

  // Cede ante pedidos (lo operativo gana). showInvite ya implica control + experimento + cadencia.
  if (!gate.showInvite || hayPedido) return null;

  return (
    <PaulinaInvite
      surface={inviteSurface}
      anchor={isDesktop ? 'desktop' : 'mobile'}
      onAccept={() => {
        // Señal one-shot ANTES de accept: el provider se remonta al pasar a treatment y la
        // consume al montar para abrir el chat. Debe ir antes de gate.accept (ver openOnMount).
        markOpenOnMount();
        gate.accept();
      }}
      onDismiss={gate.dismiss}
      onShown={gate.shown}
    />
  );
};
